import type React from "react";
import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, CheckCircle, XCircle } from "lucide-react";

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive" | "success";
  isLoading?: boolean;
  /** When set, confirm button is disabled for this many ms after dialog opens (e.g. 3000 for 3s). */
  confirmDisabledForMs?: number;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
  isLoading = false,
  confirmDisabledForMs,
}) => {
  const [confirmEnabled, setConfirmEnabled] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setConfirmEnabled(false);
      setCountdownSeconds(0);
      return;
    }
    if (confirmDisabledForMs == null || confirmDisabledForMs <= 0) {
      setConfirmEnabled(true);
      return;
    }
    setConfirmEnabled(false);
    const totalSeconds = Math.ceil(confirmDisabledForMs / 1000);
    setCountdownSeconds(totalSeconds);

    const endTime = Date.now() + confirmDisabledForMs;
    const intervalMs = 1000;

    const timeoutId = setTimeout(() => {
      setConfirmEnabled(true);
      setCountdownSeconds(0);
    }, confirmDisabledForMs);

    const intervalId = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
      setCountdownSeconds(remaining);
    }, intervalMs);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [isOpen, confirmDisabledForMs]);

  const isConfirmDisabled = isLoading || !confirmEnabled;
  const confirmButtonLabel =
    confirmDisabledForMs != null && confirmDisabledForMs > 0 && countdownSeconds > 0
      ? `${confirmText} (${countdownSeconds})`
      : confirmText;

  const getIcon = () => {
    switch (variant) {
      case "destructive":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "success":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getConfirmButtonClass = () => {
    switch (variant) {
      case "destructive":
        return "bg-red-600 hover:bg-red-700 focus:ring-red-600";
      case "success":
        return "bg-green-600 hover:bg-green-700 focus:ring-green-600";
      default:
        return "";
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {getIcon()}
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>{cancelText}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={isConfirmDisabled} className={getConfirmButtonClass()}>
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Processing...
              </>
            ) : (
              confirmButtonLabel
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
